import { Plus, Scissors, X } from "lucide-react";
import { useMemo, useState } from "react";

import {
  formatTrayFraction,
  TRAY_FRACTION_VALUES,
  type CycleRow,
  type TrayFraction,
} from "../lib/api";
import { ConfirmButton } from "../components/ConfirmButton";
import { ErrorBanner } from "../components/ErrorBanner";
import { Skeleton } from "../components/Skeleton";
import { TrayIcon } from "../components/TrayIcon";
import { todayISO } from "../lib/date";
import {
  useCreateEvent,
  useCycles,
  useDeleteCycle,
  usePatchCycle,
  useReference,
  useStartCycle,
} from "../lib/queries";
import {
  fonts,
  radius,
  shadow,
  tokens,
  TRAY_IDS,
  TRAY_LABELS,
  trayColors,
  type TrayId,
} from "../styles/theme";

type TraySlot = {
  tray: TrayId;
  active: CycleRow[];
  occupied: number;
  available: number;
};

const FRACTION_EPSILON = 1e-6;

export function TraysView() {
  const cycles = useCycles({ active: true });
  const [openTray, setOpenTray] = useState<TrayId | null>(null);
  const [mutationError, setMutationError] = useState<string | null>(null);

  const slots = useMemo<TraySlot[]>(() => {
    const byTray = new Map<TrayId, CycleRow[]>();
    for (const c of cycles.data ?? []) {
      const list = byTray.get(c.tray_id as TrayId) ?? [];
      list.push(c);
      byTray.set(c.tray_id as TrayId, list);
    }
    return TRAY_IDS.map((tray) => {
      const active = (byTray.get(tray) ?? [])
        .slice()
        .sort((a, b) => a.started_at.localeCompare(b.started_at));
      const occupied = active.reduce((sum, c) => sum + (c.tray_fraction ?? 1), 0);
      const available = Math.max(0, 1 - occupied);
      return { tray, active, occupied, available };
    });
  }, [cycles.data]);

  return (
    <div>
      {mutationError && (
        <ErrorBanner message={mutationError} onDismiss={() => setMutationError(null)} />
      )}

      {cycles.isLoading && <Skeleton count={4} height={170} />}
      {cycles.isError && (
        <ErrorBanner message={`Failed to load cycles: ${(cycles.error as Error).message}`} />
      )}

      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))",
          gap: "14px",
        }}
      >
        {slots.map((slot) => (
          <TrayCard
            key={slot.tray}
            slot={slot}
            isFormOpen={openTray === slot.tray}
            onOpenForm={() => setOpenTray(slot.tray)}
            onCloseForm={() => setOpenTray(null)}
            onError={setMutationError}
          />
        ))}
      </div>
    </div>
  );
}

function TrayCard({
  slot,
  isFormOpen,
  onOpenForm,
  onCloseForm,
  onError,
}: {
  slot: TraySlot;
  isFormOpen: boolean;
  onOpenForm: () => void;
  onCloseForm: () => void;
  onError: (msg: string) => void;
}) {
  const { tray, active, occupied, available } = slot;
  const trayColor = trayColors[tray] ?? tokens.faint;
  const isFull = available <= FRACTION_EPSILON;
  const isShared = active.length > 1;

  return (
    <div
      style={{
        background: tokens.card,
        border: `1px solid ${tokens.line}`,
        borderRadius: radius.lg,
        padding: "18px 18px 16px",
        minHeight: "180px",
        display: "flex",
        flexDirection: "column",
        gap: "12px",
        boxShadow: shadow.xs,
      }}
    >
      <div
        style={{
          display: "flex",
          alignItems: "flex-start",
          justifyContent: "space-between",
          gap: "12px",
        }}
      >
        <div style={{ minWidth: 0 }}>
          <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
            <span
              aria-hidden
              style={{
                width: "12px",
                height: "12px",
                borderRadius: radius.pill,
                background: trayColor,
              }}
            />
            <span
              style={{
                fontSize: "11px",
                letterSpacing: "0.18em",
                color: tokens.muted,
                fontWeight: 600,
                textTransform: "uppercase",
              }}
            >
              Tray {tray}
            </span>
          </div>
          <div
            style={{
              fontSize: "22px",
              color: tokens.ink,
              lineHeight: 1.15,
              marginTop: "6px",
            }}
          >
            {active.length === 0
              ? TRAY_LABELS[tray]
              : isShared
                ? `${active.length} crops`
                : active[0]!.crop}
          </div>
        </div>
        {active.length > 0 && !isShared && (
          <span
            style={{
              fontSize: "11px",
              fontWeight: 600,
              letterSpacing: "0.08em",
              textTransform: "uppercase",
              padding: "4px 10px",
              background: tokens.sageSoft,
              color: tokens.sageDeep,
              borderRadius: radius.pill,
              whiteSpace: "nowrap",
            }}
          >
            {active[0]!.phase}
          </span>
        )}
      </div>

      {active.length > 0 && (
        <div style={{ display: "flex", justifyContent: "center", padding: "4px 0 2px" }}>
          <TrayIcon
            cycles={active.map((c) => ({
              id: c.id,
              crop: c.crop,
              fraction: c.tray_fraction ?? 1,
            }))}
          />
        </div>
      )}

      {active.length === 0 ? (
        isFormOpen ? (
          <StartCycleForm
            tray={tray}
            available={1}
            onCancel={onCloseForm}
            onError={onError}
            onStarted={onCloseForm}
          />
        ) : (
          <button
            type="button"
            onClick={onOpenForm}
            style={{
              marginTop: "auto",
              padding: "12px",
              minHeight: "48px",
              background: tokens.sageSoft,
              border: "none",
              color: tokens.sageDeep,
              fontFamily: fonts.body,
              fontWeight: 600,
              fontSize: "14px",
              cursor: "pointer",
              borderRadius: radius.md,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: "8px",
              transition: "background 0.15s ease",
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = tokens.sage;
              e.currentTarget.style.color = "#fff";
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = tokens.sageSoft;
              e.currentTarget.style.color = tokens.sageDeep;
            }}
          >
            <Plus size={16} /> Start cycle
          </button>
        )
      ) : (
        <>
          {active.map((cycle) => (
            <ActiveCycleRow
              key={cycle.id}
              tray={tray}
              cycle={cycle}
              showHeader={isShared}
              onError={onError}
            />
          ))}
          {!isFull &&
            (isFormOpen ? (
              <StartCycleForm
                tray={tray}
                available={available}
                onCancel={onCloseForm}
                onError={onError}
                onStarted={onCloseForm}
              />
            ) : (
              <button
                type="button"
                onClick={onOpenForm}
                style={{
                  marginTop: "auto",
                  padding: "10px",
                  minHeight: "44px",
                  background: "transparent",
                  border: `1px dashed ${tokens.lineStrong}`,
                  color: tokens.muted,
                  fontFamily: fonts.body,
                  fontWeight: 600,
                  fontSize: "13px",
                  cursor: "pointer",
                  borderRadius: radius.md,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: "8px",
                }}
              >
                <Plus size={14} /> Add cycle ({formatTrayFraction(available)} tray free)
              </button>
            ))}
        </>
      )}
    </div>
  );
}

function ActiveCycleRow({
  tray,
  cycle,
  showHeader,
  onError,
}: {
  tray: TrayId;
  cycle: CycleRow;
  showHeader: boolean;
  onError: (msg: string) => void;
}) {
  const fraction = cycle.tray_fraction ?? 1;
  const isPartial = fraction < 1 - FRACTION_EPSILON;
  const trayColor = trayColors[tray] ?? tokens.faint;

  return (
    <div
      style={{
        borderTop: showHeader ? `1px solid ${tokens.line}` : "none",
        paddingTop: showHeader ? "10px" : 0,
        display: "flex",
        flexDirection: "column",
        gap: "8px",
      }}
    >
      {showHeader && (
        <div style={{ display: "flex", alignItems: "center", gap: "8px", flexWrap: "wrap" }}>
          <span
            aria-hidden
            style={{
              width: "8px",
              height: "8px",
              borderRadius: radius.pill,
              background: trayColor,
            }}
          />
          <span style={{ fontSize: "14px", color: tokens.ink, fontWeight: 600 }}>{cycle.crop}</span>
          {isPartial && (
            <span
              style={{
                fontSize: "11px",
                color: tokens.muted,
                fontFamily: fonts.mono,
                letterSpacing: "0.04em",
              }}
            >
              {formatTrayFraction(fraction)} tray
            </span>
          )}
          <span
            style={{
              marginLeft: "auto",
              fontSize: "10px",
              fontWeight: 600,
              letterSpacing: "0.08em",
              textTransform: "uppercase",
              padding: "3px 8px",
              background: tokens.sageSoft,
              color: tokens.sageDeep,
              borderRadius: radius.pill,
              whiteSpace: "nowrap",
            }}
          >
            {cycle.phase}
          </span>
        </div>
      )}
      <div style={{ fontSize: "13px", color: tokens.inkSoft, lineHeight: 1.6 }}>
        <div>
          <span style={{ color: tokens.faint }}>Started </span>
          {new Date(cycle.started_at).toLocaleString("en-US", {
            month: "short",
            day: "numeric",
            hour: "numeric",
            minute: "2-digit",
            timeZone: "America/New_York",
          })}
          {!showHeader && isPartial && (
            <span
              style={{
                marginLeft: "8px",
                fontFamily: fonts.mono,
                fontSize: "12px",
                color: tokens.muted,
              }}
            >
              · {formatTrayFraction(fraction)} tray
            </span>
          )}
        </div>
        {cycle.target_delivery_date && (
          <div>
            <span style={{ color: tokens.faint }}>Target delivery </span>
            {cycle.target_delivery_date}
          </div>
        )}
      </div>
      <ActiveCycleActions tray={tray} active={cycle} onError={onError} />
    </div>
  );
}

function ActiveCycleActions({
  tray,
  active,
  onError,
}: {
  tray: TrayId;
  active: CycleRow;
  onError: (msg: string) => void;
}) {
  const patchCycle = usePatchCycle();
  const deleteCycle = useDeleteCycle();
  const createEvent = useCreateEvent();
  const busy = patchCycle.isPending || deleteCycle.isPending;
  const fraction = active.tray_fraction ?? 1;
  const fractionLabel =
    fraction < 1 - FRACTION_EPSILON ? ` (${formatTrayFraction(fraction)} tray)` : "";

  const handleHarvest = () => {
    const nowIso = new Date().toISOString();
    patchCycle.mutate(
      { id: active.id, patch: { harvested_at: nowIso } },
      {
        onSuccess: () => {
          createEvent.mutate({
            date: todayISO(),
            tray_id: tray,
            crop: active.crop,
            kind: "note",
            text: `Operator harvested ${active.crop}${fractionLabel} on Tray ${tray}`,
            cycle_id: active.id,
          });
        },
        onError: (err) => onError(err.message),
      },
    );
  };

  const handleCancel = () => {
    const startedDisplay = new Date(active.started_at).toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      timeZone: "America/New_York",
    });
    createEvent.mutate(
      {
        date: todayISO(),
        tray_id: tray,
        crop: active.crop,
        kind: "note",
        text: `Operator cancelled ${active.crop}${fractionLabel} cycle on Tray ${tray} (started ${startedDisplay})`,
        cycle_id: null,
      },
      {
        onSuccess: () => {
          deleteCycle.mutate({ id: active.id }, { onError: (err) => onError(err.message) });
        },
        onError: (err) => onError(err.message),
      },
    );
  };

  return (
    <div
      style={{
        marginTop: "auto",
        display: "flex",
        gap: "8px",
        paddingTop: "10px",
        borderTop: `1px solid ${tokens.line}`,
      }}
    >
      <ConfirmButton
        ariaLabel={`Harvest ${active.crop} on tray ${tray} now`}
        onConfirm={handleHarvest}
        confirmLabel={
          <span style={{ display: "inline-flex", alignItems: "center", gap: "6px" }}>
            <Scissors size={14} /> Yes, harvest
          </span>
        }
        style={{
          ...primaryActionBtn,
          background: tokens.terracotta,
          color: "#fff",
          borderColor: tokens.terracotta,
        }}
        confirmStyle={{
          background: tokens.terracottaLight,
          color: "#fff",
          borderColor: tokens.terracottaLight,
        }}
      >
        <span style={{ display: "inline-flex", alignItems: "center", gap: "6px" }}>
          <Scissors size={14} /> {busy ? "Harvesting…" : "Harvest now"}
        </span>
      </ConfirmButton>
      <ConfirmButton
        ariaLabel={`Cancel cycle on tray ${tray}`}
        onConfirm={handleCancel}
        confirmLabel={
          <span style={{ display: "inline-flex", alignItems: "center", gap: "6px" }}>
            <X size={14} /> Yes, cancel
          </span>
        }
        style={{
          ...quietActionBtn,
          color: tokens.coralDeep,
          borderColor: tokens.coralSoft,
        }}
        confirmStyle={{ background: tokens.coral, color: "#fff", borderColor: tokens.coral }}
      >
        <span style={{ display: "inline-flex", alignItems: "center", gap: "6px" }}>
          <X size={14} /> {busy ? "…" : "Cancel"}
        </span>
      </ConfirmButton>
    </div>
  );
}

function StartCycleForm({
  tray,
  available,
  onCancel,
  onStarted,
  onError,
}: {
  tray: TrayId;
  available: number;
  onCancel: () => void;
  onStarted: () => void;
  onError: (msg: string) => void;
}) {
  const reference = useReference();
  const startCycle = useStartCycle();
  const defaultCrop = TRAY_LABELS[tray];
  const [crop, setCrop] = useState<string>(defaultCrop);
  const [soakNow, setSoakNow] = useState<boolean>(false);
  const [fraction, setFraction] = useState<TrayFraction>(() => {
    // Default to the largest fraction that fits the available space, falling
    // back to 1/8 if even that overflows (shouldn't happen — caller hides
    // the form when the tray is full — but keeps the picker safe).
    return (TRAY_FRACTION_VALUES.find((v) => v <= available + FRACTION_EPSILON) ??
      0.125) as TrayFraction;
  });

  const now = new Date().toISOString();

  const submit = () => {
    if (!crop.trim()) return;
    startCycle.mutate(
      {
        tray_id: tray,
        crop: crop.trim(),
        tray_fraction: fraction,
        started_at: now,
        soak_started_at: soakNow ? now : null,
      },
      {
        onSuccess: () => onStarted(),
        onError: (err) => onError(err.message),
      },
    );
  };

  return (
    <div
      style={{
        marginTop: "auto",
        display: "grid",
        gap: "10px",
      }}
    >
      <select
        value={crop}
        onChange={(e) => setCrop(e.target.value)}
        style={formInput}
        aria-label={`Crop for tray ${tray}`}
      >
        <option value={defaultCrop}>{defaultCrop}</option>
        {(reference.data ?? [])
          .filter((r) => r.crop !== defaultCrop)
          .map((r) => (
            <option key={r.crop} value={r.crop}>
              {r.crop}
            </option>
          ))}
      </select>
      <FractionPicker
        value={fraction}
        available={available}
        onChange={setFraction}
        label={`Fraction of tray ${tray}`}
      />
      <label
        style={{
          display: "flex",
          gap: "10px",
          alignItems: "center",
          fontSize: "13px",
          color: tokens.inkSoft,
          minHeight: "44px",
          cursor: "pointer",
        }}
      >
        <input
          type="checkbox"
          checked={soakNow}
          onChange={(e) => setSoakNow(e.target.checked)}
          style={{ width: "20px", height: "20px", accentColor: tokens.sage }}
        />
        Start soaking now
      </label>
      <div style={{ display: "flex", gap: "8px" }}>
        <button type="button" onClick={submit} style={primaryBtn} disabled={startCycle.isPending}>
          {startCycle.isPending ? "Starting…" : "Start"}
        </button>
        <button type="button" onClick={onCancel} style={ghostBtn}>
          Cancel
        </button>
      </div>
    </div>
  );
}

export function FractionPicker({
  value,
  available,
  onChange,
  label,
}: {
  value: TrayFraction;
  available: number;
  onChange: (f: TrayFraction) => void;
  label: string;
}) {
  return (
    <div
      role="group"
      aria-label={label}
      style={{ display: "flex", flexDirection: "column", gap: "6px" }}
    >
      <span
        style={{
          fontSize: "11px",
          letterSpacing: "0.14em",
          color: tokens.muted,
          fontWeight: 600,
          textTransform: "uppercase",
        }}
      >
        Tray share
      </span>
      <div role="radiogroup" aria-label={label} style={{ display: "flex", gap: "6px" }}>
        {TRAY_FRACTION_VALUES.map((option) => {
          const wouldOverflow = option > available + FRACTION_EPSILON;
          const active = value === option;
          return (
            <button
              key={option}
              type="button"
              role="radio"
              aria-checked={active}
              disabled={wouldOverflow}
              onClick={() => onChange(option)}
              title={
                wouldOverflow ? `Tray only has ${formatTrayFraction(available)} left` : undefined
              }
              style={{
                flex: 1,
                minHeight: "40px",
                padding: "0 6px",
                borderRadius: radius.sm,
                border: `1px solid ${active ? tokens.sage : tokens.lineStrong}`,
                background: active ? tokens.sageSoft : tokens.card,
                color: wouldOverflow ? tokens.faint : active ? tokens.sageDeep : tokens.inkSoft,
                fontFamily: fonts.mono,
                fontSize: "13px",
                cursor: wouldOverflow ? "not-allowed" : "pointer",
                opacity: wouldOverflow ? 0.5 : 1,
              }}
            >
              {formatTrayFraction(option)}
            </button>
          );
        })}
      </div>
    </div>
  );
}

const formInput = {
  background: tokens.card,
  border: `1px solid ${tokens.lineStrong}`,
  color: tokens.ink,
  padding: "10px 12px",
  fontFamily: "inherit",
  fontSize: "14px",
  minHeight: "44px",
  borderRadius: radius.sm,
} as const;

const primaryBtn = {
  background: tokens.sage,
  border: "none",
  color: "#fff",
  padding: "10px 20px",
  minHeight: "44px",
  fontFamily: "inherit",
  fontSize: "14px",
  cursor: "pointer",
  fontWeight: 600,
  flex: 1,
  borderRadius: radius.md,
  boxShadow: shadow.sm,
} as const;

const ghostBtn = {
  background: "transparent",
  border: `1px solid ${tokens.lineStrong}`,
  color: tokens.inkSoft,
  padding: "10px 16px",
  minHeight: "44px",
  fontFamily: "inherit",
  fontSize: "14px",
  cursor: "pointer",
  fontWeight: 500,
  borderRadius: radius.md,
} as const;

// Primary destructive action — harvest. Filled terracotta echoes the
// `harvest` event chip color in theme.ts so the action and the resulting
// timeline entry feel like the same thing.
const primaryActionBtn = {
  flex: 2,
  borderWidth: "1px",
  borderStyle: "solid",
  padding: "10px 12px",
  minHeight: "44px",
  fontFamily: fonts.body,
  fontSize: "13px",
  fontWeight: 600,
  borderRadius: radius.md,
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  cursor: "pointer",
  boxShadow: shadow.xs,
} as const;

// Quieter secondary — cancel. Same touch target (≥44px) but visually recessed
// so it doesn't compete with the harvest happy-path action.
const quietActionBtn = {
  flex: 1,
  background: "transparent",
  borderWidth: "1px",
  borderStyle: "solid",
  padding: "10px 12px",
  minHeight: "44px",
  fontFamily: fonts.body,
  fontSize: "13px",
  fontWeight: 500,
  borderRadius: radius.md,
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  cursor: "pointer",
} as const;
